// Service Worker Registration with Update Detection
// Uses @NBCUDTC/reactjs-utility-belt VersionManager for version tracking

import { logger } from "@NBCUDTC/reactjs-utility-belt/logger";

import { getCurrentAppVersion } from "@/utils/pwaVersionManager";

export interface Config {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
}

export function register(config?: Config) {
  if ("serviceWorker" in navigator) {
    // Register service worker on load
    window.addEventListener("load", () => {
      const swUrl = `${import.meta.env.BASE_URL}service-worker.js`;

      registerValidSW(swUrl, config);
    });

    // Check for updates every 60 seconds
    setInterval(() => {
      checkForUpdates();
    }, 60000);
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(async (registration) => {
      const currentVersion = await getCurrentAppVersion();
      logger.info(
        `[SW] Service worker registered: v${currentVersion}`,
        registration,
      );

      // Check for updates
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.onstatechange = () => {
          if (installingWorker.state === "installed") {
            if (navigator.serviceWorker.controller) {
              // New update available
              logger.info("[SW] New content is available; please refresh.");

              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // Content cached for offline use
              logger.info("[SW] Content is cached for offline use.");

              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        };
      };

      // Check for updates immediately
      registration.update();
    })
    .catch((error) => {
      logger.error("[SW] Error during service worker registration:", error);
    });
}

export function checkForUpdates() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.update();
    });
  }
}

export function unregister() {
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        logger.error(error.message);
      });
  }
}
